// src/App.jsx - Relationship Compass Main Application

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import { AuthProvider } from './context/AuthContext';
import { initializeGamification } from './stores/gamificationStore';
import { AnalyticsConsentNotice } from '@ddd/analytics';
import { setAnalyticsPreference } from './services/analytics';
import { JOIN_PATH } from './services/joinLink';
import { DDD_TERMS_URL } from './config/legalLinks';

// Simple SkipLink component for accessibility
const SkipLink = ({ href }) => (
  <a href={href} className="skip-link">
    Skip to main content
  </a>
);

// Layout
import Layout from './components/layout/Layout';

// Pages
import HomePage from './pages/HomePage';
import DashboardPage from './pages/DashboardPage';
import AssessmentPage from './pages/AssessmentPage';
import ModulesPage from './pages/ModulesPage';
import ModuleDetailPage from './pages/ModuleDetailPage';
import ToolkitPage from './pages/ToolkitPage';
import ResourcesPage from './pages/ResourcesPage';
import DataPrivacyPage from './pages/DataPrivacyPage';
import CoachingPage from './pages/CoachingPage';
import CoachingPrivacyPage from './pages/CoachingPrivacyPage';

// Styles
import './styles/globals.css';

// Terms are DDD-wide. /terms is kept so old links and bookmarks still work.
const ExternalRedirect = ({ to, label }) => {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <p className="text-slate-300">
        Taking you to the <a href={to} className="text-rose-300 underline">{label}</a>…
      </p>
    </div>
  );
};

function App() {
  // Initialize gamification system on app load
  useEffect(() => {
    initializeGamification();
  }, []);

  return (
    <AuthProvider>
      <AppProvider>
        <Router>
          <SkipLink href="#main-content" />
          <Routes>
            {/* Public Routes */}
            <Route element={<Layout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/assessment" element={<AssessmentPage />} />
              {/* Partner join links and QR codes from SharedAssessmentModal point here.
                  main.jsx moves the code out of the URL before analytics starts,
                  so the page normally renders on the code-free path. */}
              <Route path="/assessment/values-alignment/join/:sessionCode" element={<AssessmentPage />} />
              <Route path={JOIN_PATH} element={<AssessmentPage />} />
              <Route path="/modules" element={<ModulesPage />} />
              <Route path="/modules/:moduleId" element={<ModuleDetailPage />} />
              <Route path="/toolkit" element={<ToolkitPage />} />
              <Route path="/resources" element={<ResourcesPage />} />
              <Route path="/coach" element={<CoachingPage />} />
              <Route path="/coach/privacy" element={<CoachingPrivacyPage />} />
              <Route path="/privacy" element={<DataPrivacyPage />} />
              {/* The header's Settings button once pointed here, a route that never existed. */}
              <Route path="/settings" element={<Navigate to="/privacy?tab=settings" replace />} />
              <Route path="/terms" element={<ExternalRedirect to={DDD_TERMS_URL} label="Dive Deep Dialogues Terms of Use" />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <AnalyticsConsentNotice
            appName="Relationship Compass"
            privacyHref="/privacy"
            accent="#f43f5e"
            onDecision={setAnalyticsPreference}
          />
        </Router>
      </AppProvider>
    </AuthProvider>
  );
}

export default App;
